/**
 * The node's subscription status (#38).
 *
 * Gated by the feature_subscription node toggle: when the node has the feature
 * off the backend doesn't mount /api/billing at all, so we skip the fetch and
 * report the not-subscribed default rather than logging a 404 on every mount.
 * `featureSubscription` is permissive-until-known, so the request still fires
 * on the first render of a node that has it on.
 *
 *   const { is_subscriber } = useSubscription();
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNodeInfo } from './use_node_info';
import getEnv from '../env';

const API_URL = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';

const NOT_SUBSCRIBED = {
    is_subscriber: false,
    subscription_status: null,
    subscription_tier: null,
};

export function useSubscription() {
    const { isAuthenticated, user } = useAuth();
    const { featureSubscription } = useNodeInfo();
    const [status, setStatus] = useState(NOT_SUBSCRIBED);

    useEffect(() => {
        if (!featureSubscription) { setStatus(NOT_SUBSCRIBED); return; }
        if (!isAuthenticated) return;
        const userId = user?.id;
        if (!userId) return;

        fetch(`${API_URL}/api/billing/status?user_id=${encodeURIComponent(userId)}`)
            .then(res => {
                if (res.ok) return res.json();
                throw new Error('Failed to fetch subscription status');
            })
            .then(data => setStatus(data))
            .catch(err => console.error('Subscription status error:', err));
    }, [isAuthenticated, user?.id, featureSubscription]);

    return status;
}
